<template>
  <div class="planningSelection-container">
    <div class="header" style="margin-bottom:10px">
      <el-steps :active="currentWorkshopIndex" simple align-center>
        <el-step
          v-for="(step, idx) in workshopsInPlanning"
          :key="idx"
          :title="step.workshopName"
          :status="getStepStatus(idx)"
        ></el-step>
      </el-steps>
    </div>
    <div v-loading="pageLoading" class="planningSelection-main-container">
      <el-table
        ref="planningSelectionList"
        :data="items"
        border
        fit
        highlight-current-row
        style="width: 100%;"
        height="600"
        @selection-change="handleSelectionChange"
      >
        <el-table-column :label="$t('planning.yarnDescription')" property="yarnDescription">
          <template slot-scope="scope">
            <span>{{ scope.row['yarnDescription'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('orderLine.fabricStatus')" property="fabricStatus">
          <template slot-scope="scope">
            <span>{{ formatFabricStatus(scope.row['fabricStatus']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('planning.fabricCode')" property="fabricCode">
          <template slot-scope="scope">
            <span>{{ scope.row['fabricCode'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('planning.fabricDescription')" property="fabricDescription">
          <template slot-scope="scope">
            <span>{{ scope.row['fabricDescription'] }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('planning.workshopAvailableAmountInMeters')"
          property="workshopAvailableAmountInMeters"
        >
          <template slot-scope="scope">
            <span>{{ scope.row['workshopAvailableAmountInMeters'] }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('planning.distributionCenterAvailableAmountInMeters')"
          property="distributionCenterAvailableAmountInMeters"
        >
          <template slot-scope="scope">
            <span>{{ scope.row['distributionCenterAvailableAmountInMeters'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('planning.domainAvailableAmountInMeters')" property="domainAvailableAmountInMeters">
          <template slot-scope="scope">
            <span>{{ scope.row['domainAvailableAmountInMeters'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('planning.requiredFabric')" property="requiredFabric">
          <template slot-scope="scope">
            <span>{{ scope.row['requiredFabric'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="amountString" property="requiredCapacity">
          <template slot-scope="scope">
            <span>{{ scope.row['requiredCapacity'] }}</span>
          </template>
        </el-table-column>
        <el-table-column type="selection" width="40" align="center" :selectable="() => true"></el-table-column>
      </el-table>
      <div style="margin-top:5px;clear:both;">
        <div style="width:30%;float:left;">
          <label class="selectionFooterLabel">{{ $t('planning.newProposalWithFilter') }} </label>
          <div style="position:relative">
            <el-select
              v-model="selectedYarnIDs"
              :multiple="true"
              type="select"
              :disabled="selectByDateYN"
              :placeholder="$t('common.yarn')"
              clearable
            >
              <el-option-group v-if="plannableYarns.length" :label="$t('common.yarn')">
                <el-option
                  v-for="yarn in plannableYarns"
                  :key="yarn.yarnID"
                  :label="yarn.description"
                  :value="yarn.yarnID"
                />
              </el-option-group>
            </el-select>

            <label>
              <el-checkbox
                v-model="strictlySelectedYarns"
                :disabled="selectByDateYN"
                style="margin-left:20px;"
              ></el-checkbox>
              {{ $t('planning.strictlySelectedYarns') }}
            </label>
          </div>
          <div style="position:relative">
            <label style="display:block;">
              {{ $t('planning.excludeDiverseLining') }}
              <el-checkbox
                v-model="excludeDiverseLiningYN"
                :disabled="selectByDateYN"
                style="margin-left:20px;"
              ></el-checkbox>
            </label>
          </div>
          <div style="position:relative">
            <label style="display:block;">
              {{ $t('planning.basedOnOrderDate') }}
              <el-checkbox
                v-model="selectByDateYN"
                :disabled="selectedYarnIDs.length > 0 || excludeDiverseLiningYN"
                style="margin-left:20px;"
                @change="handleSelectByDateYN"
              ></el-checkbox>
            </label>
          </div>
        </div>
        <div style="width:50%;float:left;">
          <label class="selectionFooterLabel">{{ $t('planning.planningDateRange') }}: </label>
          <el-date-picker
            v-model="preselectionRange"
            readonly
            type="daterange"
            format="dd-MM-yyyy"
            value-format="yyyy-MM-ddTHH:mm:ss"
          >
          </el-date-picker>
        </div>
        <div style="width:20%;float:left;text-align:right;">
          <label class="capacity-footers">
            {{ totalCapacitySelected + ' / ' + maxCapacityWorkshop }}
          </label>
        </div>
      </div>
      <div style="padding-top:10px;clear:both;">
        <div style="float:left">
          <editor-button
            :icon-only="false"
            :disabled="pageLoading"
            button-translation="planning.generate"
            action="primary"
            style="float:right;"
            @click="generateFilteredProposal()"
          />
        </div>
        <div style="width:30%;float:right;text-align:right;">
          <editor-button
            :icon-only="false"
            :disabled="pageLoading"
            button-translation="planning.makeSelection"
            action="primary"
            style="float:right;"
            @click="makeSelection()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EditorButton from '@/components/crud/EditorButton';
import { closeCurrentTag } from '@/utils/tags';
import { PlanningOption } from '@/utils/constants';
import { getOptionDescription } from '@/utils/store';

export default {
  name: 'PlanningSelectionList',
  components: {
    EditorButton
  },
  props: {
    permissionCode: String,
    domainID: Number,
    selectedPlanningOption: Number,
    excludedStoreIDs: {
      type: Array,
      default: () => []
    },
    excludedStoreAttributeIDs: {
      type: Array,
      default: () => []
    },
    installationServiceOption: Number,
    samplesOnlyYN: Boolean
  },
  data() {
    return {
      pageLoading: false,

      workshopsInPlanning: [],
      currentDomain: '',
      currentWorkshopID: 0,
      currentWorkshopIndex: 0,
      items: [],
      totalCapacitySelected: 0,
      maxCapacityWorkshop: 0,
      capacityInSelection: 0,
      amountString: '',

      plannableYarns: [],
      selectedYarnIDs: [],
      strictlySelectedYarns: false,
      selectByDateYN: false,
      excludeDiverseLiningYN: false,
      preselectionRange: [],
      multipleSelection: []
    };
  },
  watch: {
    selectedPlanningOption() {
      this.getRequiredAmountTranslation();
      this.oneTimeSetup();
    }
  },
  created() {
    this.$store.dispatch('productionPlanning/getPlannableYarns').then(data => {
      this.plannableYarns = data;
    });
  },
  methods: {
    toggleSelection() {
      this.items
        .filter(i => i.selectedYN)
        .forEach(row => {
          this.$refs.planningSelectionList.toggleRowSelection(row, true);
        });
    },
    handleSelectionChange(val) {
      this.totalCapacitySelected = val.reduce((sum, row) => (sum += row.requiredCapacity), 0);
      this.multipleSelection = val;
    },
    async oneTimeSetup() {
      this.pageLoading = true;
      await this.$store.dispatch('productionPlanningSelection/postMakePreselectionForPlanningOption', {
        planningOption: this.selectedPlanningOption,
        domainID: this.domainID,
        excludedStoreIDs: this.excludedStoreIDs,
        excludedStoreAttributeIDs: this.excludedStoreAttributeIDs,
        installationServiceOption: this.installationServiceOption,
        samplesOnlyYN: this.samplesOnlyYN
      });
      await this.$store
        .dispatch('productionPlanningSelection/getWorkshopsInPlanning', {
          domainID: this.domainID,
          planningOption: this.selectedPlanningOption
        })
        .then(data => {
          const currentWorkshop = data.find(w => !w.selectionMadeYN);
          if (typeof currentWorkshop === 'undefined') {
            this.$emit('action', 'startSeriesStep');
          } else {
            this.currentWorkshopID = currentWorkshop.workshopID;
            this.workshopsInPlanning = data;
            this.currentWorkshopIndex = this.workshopsInPlanning.findIndex(
              w => w.workshopID === this.currentWorkshopID
            );
            this.getProposalForWorkshop(this.currentWorkshopID);
          }
        });
    },
    getProposalForWorkshop(workshopID) {
      this.pageLoading = true;
      this.$store
        .dispatch('productionPlanningSelection/getProposalForWorkshop', {
          domainID: this.domainID,
          workshopID: workshopID,
          planningOption: this.selectedPlanningOption
        })
        .then(data => {
          this.currentDomain = data.domainDescription;
          this.totalCapacitySelected = data.capacitySelected;
          this.maxCapacityWorkshop = data.capacityForWorkshop;
          this.preselectionRange = [data.dateMin, data.dateMax];
          this.items = data.fabricsInSelection;
          this.$nextTick(() => {
            this.toggleSelection();
          });
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    makeSelection() {
      this.pageLoading = true;
      this.$store
        .dispatch('productionPlanningSelection/postSelectionForWorkshop', {
          domainID: this.domainID,
          workshopID: this.currentWorkshopID,
          planningOption: this.selectedPlanningOption,
          selection: this.multipleSelection.map(i => i.fabricID),
          excludeDiverseLiningYN: this.excludeDiverseLiningYN,
          selectByDateYN: this.selectByDateYN
        })
        .then(() => {
          const currentWorkshopIndex = this.workshopsInPlanning.findIndex(w => w.workshopID === this.currentWorkshopID);
          this.workshopsInPlanning[currentWorkshopIndex].selectionMadeYN = true;
          const nextWorkshopIndex = currentWorkshopIndex + 1;
          if (nextWorkshopIndex < this.workshopsInPlanning.length) {
            this.selectByDateYN = false;
            this.excludeDiverseLiningYN = false;
            this.selectedYarnIDs = [];
            this.strictlySelectedYarns = false;
            const nextWorkshopID = this.workshopsInPlanning[nextWorkshopIndex].workshopID;
            this.getProposalForWorkshop(nextWorkshopID);
            this.currentWorkshopIndex = nextWorkshopIndex;
            this.currentWorkshopID = nextWorkshopID;
          } else {
            this.$emit('action', 'startSeriesStep', { planningOption: this.selectedPlanningOption });
          }
        });
    },
    close() {
      closeCurrentTag(this);
    },
    clearAllFilters() {
      this.$refs.planningSelectionList.clearFilter();
      this.search = {};
    },
    getStepStatus(idx) {
      const workshop = this.workshopsInPlanning[idx];
      if (workshop.selectionMadeYN) {
        return 'success';
      } else if (idx === this.currentWorkshopIndex) {
        return 'finish';
      } else {
        return 'wait';
      }
    },
    getRequiredAmountTranslation() {
      switch (this.selectedPlanningOption) {
        case PlanningOption.Panel:
          this.amountString = this.$i18n.t('planning.amount') + ' ' + this.$i18n.t('planning.panels');
          break;
        case PlanningOption.NetCurtainRoomHigh:
          this.amountString = this.$i18n.t('planning.amount') + ' ' + this.$i18n.t('planning.netCurtainsMeters');
          break;
        case PlanningOption.CurtainRoomHigh:
          this.amountString = this.$i18n.t('planning.amount') + ' ' + this.$i18n.t('planning.curtainsMeters');
          break;
        case PlanningOption.RomanBlind:
          this.amountString = this.$i18n.t('planning.amount') + ' ' + this.$i18n.t('planning.romanBlinds');
          break;
        case PlanningOption.Cutting:
          this.amountString = this.$i18n.t('planning.amount') + ' ' + this.$i18n.t('planning.cuttings');
          break;
      }
    },
    generateFilteredProposal() {
      this.pageLoading = true;
      this.$store
        .dispatch('productionPlanningSelection/generateFilteredSelectionForWorkshop', {
          domainID: this.domainID,
          workshopID: this.currentWorkshopID,
          planningOption: this.selectedPlanningOption,
          yarnIDs: this.selectedYarnIDs,
          strictlySelectedYarns: this.strictlySelectedYarns,
          selectByDateYN: this.selectByDateYN,
          excludeDiverseLiningYN: this.excludeDiverseLiningYN
        })
        .then(data => {
          this.totalCapacitySelected = data.capacitySelected;
          this.items = data.fabricsInSelection;
          this.$nextTick(() => {
            this.toggleSelection();
          });
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    handleSelectByDateYN(value) {
      if (value) {
        this.$store
          .dispatch('notify/confirm', {
            message: this.$i18n.t('planning.inefficientPlanning'),
            confirmButtonText: this.$i18n.t('common.continue')
          })
          .catch(() => {
            this.selectByDateYN = false;
          });
      }
    },
    formatFabricStatus(cellValue) {
      return getOptionDescription(this.$store, 'fabricStatus', cellValue);
    }
  }
};
</script>
<style lang="scss">
.clear-input-button {
  float: right;
  background-color: #ff8c8c;
  color: white;

  &:hover {
    background-color: #ffb3b3;
    color: white;
  }
}

.planningSelection-container {
  background-color: white;

  .selectionFooterLabel {
    display: block;
    padding-bottom: 8px;
    font-size: 14px !important;
  }

  .capacity-footers {
    color: #606266;
    display: block;
    margin-top: 10px !important;
    font-size: 22px !important;
  }
  .search {
    .el-input__inner {
      padding-left: 25px;
      padding-right: 20px;
    }

    .el-input__prefix {
      left: 0;
    }

    .el-input__suffix {
      right: 0;
    }
  }

  .search-select {
    width: 100%;

    .el-input__inner {
      padding-left: 5px;
    }

    .el-input {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .el-input__suffix {
      top: -2px;
      right: 10px;
    }
  }

  .el-table .cell {
    min-height: 3em !important;
  }

  .el-table .cell {
    word-break: break-word;
  }

  .el-table .el-table__row {
    background-color: white;
  }

  .el-table tbody tr:hover .cell {
    color: initial;
  }

  .el-table .input-hidden {
    visibility: hidden;
  }
}
</style>
